/**
 * Zapier domain detection utility.
 *
 * Determines if the current execution context is on a Zapier-controlled domain.
 * Used to prevent CORS errors when making API calls from non-Zapier domains.
 *
 * @module isZapierDomain
 */

/**
 * Checks if the current hostname is a Zapier domain.
 *
 * Matches production and staging Zapier domains:
 * - zapier.com and subdomains (*.zapier.com)
 * - zapier-staging.com and subdomains (*.zapier-staging.com)
 * - zapier-deployment.com and subdomains (*.zapier-deployment.com)
 * - localhost (for local development)
 *
 * @returns {boolean} True if on a Zapier domain, false otherwise
 *
 * @example
 * ```typescript
 * if (isZapierDomain()) {
 *   // Safe to make API calls to Zapier
 *   await fetch('/api/v4/session');
 * }
 * ```
 */
export function isZapierDomain(): boolean {
  if (typeof window === "undefined") {
    return false; // SSR context
  }

  const hostname = window.location.hostname;

  return (
    hostname === "localhost" ||
    hostname.endsWith(".zapier.com") ||
    hostname === "zapier.com" ||
    hostname.endsWith(".zapier-staging.com") ||
    hostname === "zapier-staging.com" ||
    hostname.endsWith(".zapier-deployment.com") ||
    hostname === "zapier-deployment.com"
  );
}
